import { ActionBar, Button, Checkbox, Dialog, Label, Modal, Select, Text } from '@viapeople/components'
import { useEffect, useRef, useState } from 'react'
import { formatStepName } from '../../../utils/helpers'
import { useToast } from '../../../utils/ToastContext'
import { useMoveEvaluationsStore } from '../stores/moveEvaluations.store'

export type EligibleEvaluation = {
	evaluationId: string
	participantId: string
	participantName: string
	participantEmail: string
	currentStepId: string
	currentStepName: string
	hasSubmissions: boolean
	taskStatus: string
}

export type PreviousStepOption = {
	stepId: string
	stepType: string
	name: string
	order: number
}

export type CurrentCycleStep = {
	stepId: string
	stepName: string
	stepType: string
	evaluationCount: number
}

interface MoveEvaluationBackModalProps {
	isOpen: boolean
	onClose: () => void
	cycleId: string
	organizationId: string
	useStore?: typeof useMoveEvaluationsStore
}

export function MoveEvaluationsModal({
	isOpen,
	onClose,
	cycleId,
	useStore = useMoveEvaluationsStore,
}: MoveEvaluationBackModalProps) {
	const [showConfirmation, setShowConfirmation] = useState(false)
	const { showToast } = useToast()
	const mountedRef = useRef(false)
	const {
		sourceSteps,
		sourceStepId,
		possibleMoves,
		evaluations,
		moveInstructions,
		movePreview,
		error,
		initialize,
		setSourceStepId,
		setMoveInstructions,
		moveEvaluations,
		clearError,
	} = useStore()

	useEffect(() => {
		if (!mountedRef.current) {
			mountedRef.current = true

			if (cycleId) {
				initialize(cycleId)
			}
		}
	})

	useEffect(() => {
		if (error) {
			showToast(error, 'error')
			clearError()
		}
	}, [error, clearError, showToast])

	const resetForm = () => {
		setSourceStepId()
		setMoveInstructions({
			evaluationIds: [],
			discardSubmissions: false,
			applyToAll: false,
			targetStepId: undefined,
			reason: undefined,
		})
		setShowConfirmation(false)
	}

	const handleClose = () => {
		resetForm()
		onClose()
		mountedRef.current = false
	}

	const handleSelectAll = () => {
		setMoveInstructions({
			evaluationIds: evaluations.map((e) => e.evaluationId),
		})
	}

	const handleConfirm = () => {
		if (!sourceStepId || !moveInstructions.targetStepId || moveInstructions?.evaluationIds.length === 0) return
		setShowConfirmation(true)
	}

	const handleExecuteMove = () => {
		moveEvaluations(() => {
			setShowConfirmation(false)
			handleClose()
		})
	}

	const selectedStep = possibleMoves.find((step: PreviousStepOption) => step.stepId === moveInstructions?.targetStepId)

	const handleEvaluationToggle = (evaluation: EligibleEvaluation) => {
		const isSelected = moveInstructions.evaluationIds.some((e) => e === evaluation.evaluationId)
		if (isSelected) {
			setMoveInstructions({
				evaluationIds: moveInstructions.evaluationIds.filter((e) => e !== evaluation.evaluationId),
			})
		} else {
			setMoveInstructions({
				evaluationIds: [...moveInstructions.evaluationIds, evaluation.evaluationId],
			})
		}
	}

	return (
		<>
			<Modal isOpen={isOpen} onClose={handleClose} title='Move Evaluations' className='!w-[90vw] !max-w-5xl'>
				<div className='space-y-4 pb-24'>
					{/* Current Step Selection */}
					<div>
						<Text variant='small' weight='semibold' className='mb-2'>
							Select current step to move from:
						</Text>
						<Select
							label='Current Step'
							value={sourceStepId}
							onChange={setSourceStepId}
							options={sourceSteps.map((step: CurrentCycleStep) => ({
								value: step.stepId,
								label: `${formatStepName(step.stepName)} (${step.evaluationCount} evaluations)`,
							}))}
							placeholder='Select the step evaluations are currently at'
						/>
					</div>

					{/* Step Selection */}
					{sourceStepId && (
						<>
							<div>
								<Text variant='small' weight='semibold' className='mb-2'>
									Select target step:
								</Text>
								<Select
									label='Target Step'
									value={moveInstructions?.targetStepId ?? ''}
									onChange={(targetStepId) =>
										setMoveInstructions({
											targetStepId,
										})
									}
									options={(() => {
										// Count occurrences of each step name to add numbers for duplicates
										const nameCounts: Record<string, number> = {}
										const nameIndexes: Record<string, number> = {}

										possibleMoves.forEach((step: PreviousStepOption) => {
											nameCounts[step.name] = (nameCounts[step.name] || 0) + 1
										})

										return possibleMoves.map((step: PreviousStepOption) => {
											const formattedName = formatStepName(step.name)
											let label = formattedName

											// If there are multiple steps with the same name, add a number
											if (nameCounts[step.name] > 1) {
												nameIndexes[step.name] = (nameIndexes[step.name] || 0) + 1
												label = `${formattedName} #${nameIndexes[step.name]}`
											}

											return {
												value: step.stepId,
												label,
											}
										})
									})()}
									placeholder='Select target step'
								/>
							</div>

							{/* Evaluation Selection */}
							<div>
								<div className='flex items-center justify-between mb-2'>
									<Text variant='small' weight='semibold'>
										Select evaluations to move:
									</Text>
									<Button variant='tertiary' onClick={handleSelectAll} size='small'>
										{moveInstructions?.evaluationIds.length === evaluations.length ? 'Deselect All' : 'Select All'}
									</Button>
								</div>

								<div className='border border-gray-200 rounded-lg max-h-96 overflow-y-auto'>
									{evaluations.length === 0 ? (
										<div className='p-8 text-center'>
											<Text variant='small' color='gray-500'>
												No evaluations are currently at this step
											</Text>
										</div>
									) : (
										<div className='divide-y divide-gray-200'>
											{evaluations.map((evaluation: EligibleEvaluation, index: number) => {
												const isSelected = moveInstructions?.evaluationIds.some((e) => e === evaluation.evaluationId)
												return (
													<button
														type='button'
														key={`${evaluation.evaluationId}-${index}`}
														className='p-4 flex items-center space-x-3 hover:bg-gray-50 cursor-pointer'
														onClick={() => handleEvaluationToggle(evaluation)}>
														<Checkbox checked={isSelected} onChange={() => handleEvaluationToggle(evaluation)} />
														<div className='flex-1 min-w-0'>
															<div className='flex items-center justify-between'>
																<div>
																	<Text variant='small' weight='semibold'>
																		{evaluation.participantName}
																	</Text>
																	<Text variant='xsmall' color='neutral-600'>
																		{evaluation.participantEmail}
																	</Text>
																</div>
																<div className='flex items-center space-x-2'>
																	<Text variant='xsmall' color='neutral-500'>
																		{formatStepName(evaluation.currentStepName)}
																	</Text>
																	<Label
																		color={
																			evaluation.taskStatus === 'completed'
																				? 'green'
																				: evaluation.taskStatus === 'in-progress'
																					? 'yellow'
																					: 'default'
																		}>
																		{evaluation.taskStatus.replace('-', ' ')}
																	</Label>
																	<Label color={evaluation.hasSubmissions ? 'green' : 'default'}>
																		{evaluation.hasSubmissions ? 'Has Submissions' : 'No Submissions'}
																	</Label>
																</div>
															</div>
														</div>
													</button>
												)
											})}
										</div>
									)}
								</div>
							</div>

							{/* Options */}
							<div className='space-y-3'>
								<div className='flex items-center space-x-2'>
									<Checkbox
										checked={moveInstructions?.discardSubmissions}
										onChange={(e) =>
											setMoveInstructions({
												discardSubmissions: e.target.checked,
												...(e.target.checked ? {} : { applyToAll: false }),
											})
										}
										label='Discard submissions'
									/>
								</div>

								<div className='flex items-center space-x-2 ml-6'>
									<Checkbox
										checked={moveInstructions?.applyToAll}
										disabled={!moveInstructions?.discardSubmissions}
										onChange={(e) =>
											setMoveInstructions({
												applyToAll: e.target.checked,
											})
										}
										label='Apply to all steps'
									/>
								</div>

								<div>
									<Text variant='small' weight='semibold' className='mb-1'>
										Reason (optional):
									</Text>
									<textarea
										className='w-full border rounded-lg p-2 text-sm'
										rows={3}
										value={moveInstructions?.reason ?? ''}
										onChange={(e) =>
											setMoveInstructions({
												reason: e.target.value,
											})
										}
										placeholder='Enter reason for moving evaluations...'
									/>
								</div>
							</div>

							{/* Preview */}
							{movePreview && selectedStep && (
								<div className='bg-blue-50 p-4 rounded-lg'>
									<Text variant='small' weight='semibold' className='mb-2'>
										Preview:
									</Text>
									<div className='space-y-1'>
										<Text variant='xsmall'>
											Moving {movePreview.affectedEvaluations.length} evaluations to "
											{formatStepName(selectedStep.name)}"
										</Text>
										<Text variant='xsmall'>Submissions to mark as stale: {movePreview.submissionsToStale}</Text>
										<Text variant='xsmall'>Completed tasks to mark as stale: {movePreview.tasksToStale}</Text>
										<Text variant='xsmall'>In-progress tasks to delete: {movePreview.tasksToDelete}</Text>
									</div>
								</div>
							)}
						</>
					)}
				</div>

				<ActionBar
					variant='modal'
					rightActions={[
						{
							label: 'Cancel',
							variant: 'secondary',
							onClick: handleClose,
						},
						{
							label: 'Move Evaluations',
							variant: 'primary',
							onClick: handleConfirm,
							disabled:
								!sourceStepId || !moveInstructions?.targetStepId || moveInstructions?.evaluationIds.length === 0,
						},
					]}
				/>
			</Modal>

			{/* Confirmation Dialog */}
			<Dialog
				isOpen={showConfirmation}
				onClose={() => setShowConfirmation(false)}
				onConfirm={handleExecuteMove}
				title='Confirm Move'
				message={`Are you sure you want to move ${moveInstructions?.evaluationIds.length} evaluation(s) to "${formatStepName(selectedStep?.name || '')}"? This action cannot be undone. ${moveInstructions?.discardSubmissions ? (moveInstructions?.applyToAll ? 'All submissions for the selected evaluations will be discarded.' : 'Submissions from the current and in-between steps will be discarded.') : 'All existing submissions will be preserved.'}`}
				type='delete'
				confirmLabel='Move Evaluations'
			/>
		</>
	)
}
